import React from 'react';
import { motion } from 'framer-motion';

const FLAME = 'M50 2 C58 20 72 26 74 44 C78 38 80 30 79 24 C90 38 96 56 92 74 C88 98 70 116 50 116 C30 116 12 98 8 74 C4 56 10 38 21 24 C20 30 22 38 26 44 C28 26 42 20 50 2 Z';
const LAYERS = [
  { id: 'o', scale: 1, from: '#B978FF', mid: '#D4AF37', to: '#FFE9A0', op: 0.55, dur: 1.6 },
  { id: 'm', scale: 0.86, from: '#D4AF37', mid: '#F2C14E', to: '#FFF4C2', op: 0.6, dur: 1.2 },
  { id: 'i', scale: 0.7, from: '#FFE9A0', mid: '#FFF4C2', to: '#FFFFFF', op: 0.5, dur: 0.9 },
];

/* Crystal-gold sovereign flame that wraps the user's portrait — translucent centre so the face shows through. */
export const SovereignFlame = ({ size = 160, children }) => {
  const uid = React.useId().replace(/:/g, '');
  const ring = size * 0.5;
  return (
    <div className="relative" style={{ width: size, height: size * 1.2 }}>
      <svg className="absolute inset-0" width={size} height={size * 1.2} viewBox="0 0 100 120" style={{ overflow: 'visible' }}>
        <defs>
          {LAYERS.map((l) => (
            <linearGradient key={l.id} id={`${uid}${l.id}`} x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor={l.from} />
              <stop offset="55%" stopColor={l.mid} />
              <stop offset="100%" stopColor={l.to} />
            </linearGradient>
          ))}
          <radialGradient id={`${uid}hole`} cx="50" cy="78" r="34" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#000" />
            <stop offset="62%" stopColor="#000" />
            <stop offset="100%" stopColor="#fff" />
          </radialGradient>
          <mask id={`${uid}m`}><rect x="-20" y="-20" width="140" height="160" fill={children ? `url(#${uid}hole)` : '#fff'} /></mask>
          <filter id={`${uid}g`} x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="1.6" /></filter>
        </defs>
        <g mask={`url(#${uid}m)`} filter={`url(#${uid}g)`}>
          {LAYERS.map((l, i) => (
            <motion.path key={l.id} d={FLAME} fill={`url(#${uid}${l.id})`} opacity={l.op}
              style={{ transformOrigin: '50px 116px', transformBox: 'view-box' }}
              initial={{ scale: l.scale }}
              animate={{ scaleY: [l.scale, l.scale * 1.06, l.scale * 0.97, l.scale], scaleX: [l.scale, l.scale * 0.97, l.scale * 1.03, l.scale], skewX: [0, i % 2 ? 2 : -2, 0] }}
              transition={{ duration: l.dur, repeat: Infinity, ease: 'easeInOut' }} />
          ))}
        </g>
        <path d={FLAME} fill="none" stroke="#FFE9A0" strokeOpacity="0.35" strokeWidth="0.6" />
      </svg>
      {children && (
        <div className="absolute rounded-full overflow-hidden" data-testid="sovereign-flame-portrait"
          style={{ width: ring, height: ring, left: (size - ring) / 2, top: size * 1.2 * (78 / 120) - ring / 2,
            boxShadow: '0 0 24px rgba(255,233,160,0.55), inset 0 0 0 2px #D4AF37' }}>
          {children}
        </div>
      )}
    </div>
  );
};

/* Portrait content for the flame: profile photo or gold initial. */
export const Portrait = ({ user }) => (user?.profile_picture
  ? <img src={user.profile_picture} alt={user.nickname || 'You'} className="w-full h-full object-cover" />
  : <div className="w-full h-full flex items-center justify-center font-['Cinzel'] text-3xl" style={{ background: 'radial-gradient(circle at 40% 35%, #B978FF, #3A0F5E)', color: '#FFE9A0' }}>{(user?.nickname || 'B')[0].toUpperCase()}</div>);

export default SovereignFlame;
